/**
 * 🚀 完整的沙箱管理器（整合 SuggestionApplicator 和 NewSuggestionFactory）
 *
 * 这个文件完全替代了：
 * - SuggestionApplicator
 * - NewSuggestionFactory
 *
 * 核心功能：
 * 1. 使用临时 YDoc 作为沙箱
 * 2. 在沙箱中进行所有 block 操作
 * 3. 支持 AbortController 中断机制
 * 4. 计算差异并原子性地提交到主文档
 */

import {
  Doc as YDoc,
  applyUpdate,
  encodeStateAsUpdate,
  encodeStateVector,
  Map as YMap,
  Array as YArray,
} from 'yjs';
import type { Store } from '@blocksuite/affine/store';
import {
  native2Y,
  isPureObject,
  internalPrimitives,
} from '@blocksuite/affine/store';
import type { EditorHost } from '@blocksuite/affine/std';
import { BaseBlockTransformer } from '@blocksuite/affine/store';
import type { Suggestion } from '../types/suggestion.d';
import {
  MixTextAdapter,
  titleMiddleware,
  embedSyncedDocMiddleware,
} from '@blocksuite/affine/shared/adapters';
import { nanoid } from '@blocksuite/affine/store';

/**
 * 🔑 主沙箱管理器类
 */
export class SuggestionSandboxManager {
  // 批次管理
  private _currentBatchId: string | null = null;
  private _currentBatchCreatedAt: number | null = null;
  private _currentAbortController: AbortController | null = null;

  constructor(
    private readonly store: Store,
    _host?: EditorHost
  ) {
    // Host 参数保留供将来使用（如自定义 transformer 等）
    console.log(
      '✅ SuggestionSandboxManager 初始化完成',
      _host ? '(with host)' : ''
    );
  }

  /**
   * 🔑 核心方法：在沙箱中批量应用 suggestions（可中断）
   */
  async applyBatchInSandbox(
    suggestions: Suggestion[],
    batchId: string,
    options?: {
      onProgress?: (progress: number, message: string) => void;
    }
  ): Promise<{
    success: boolean;
    results: Map<string, any>;
    aborted?: boolean;
  }> {
    // ============================================
    // 步骤1: 中断旧批次，启动新批次（增强版）
    // ============================================

    // ✅ 获取当前批次的 createdAt（取所有 suggestions 的最大值）
    const incomingCreatedAt = Math.max(...suggestions.map(s => s.createdAt));

    // ✅ 判断是否需要中断
    let shouldAbort = false;
    let abortReason = '';

    if (this._currentBatchId && this._currentAbortController) {
      if (this._currentBatchId !== batchId) {
        // 情况1: 不同 batch_id，直接中断
        shouldAbort = true;
        abortReason = `新批次 ${batchId} 到达（不同 batch_id）`;
      } else if (
        this._currentBatchCreatedAt !== null &&
        incomingCreatedAt > this._currentBatchCreatedAt
      ) {
        // 情况2: 相同 batch_id，但 createdAt 更新（流式更新）
        shouldAbort = true;
        abortReason = `检测到更新内容（batch_id: ${batchId}, 旧时间: ${this._currentBatchCreatedAt}, 新时间: ${incomingCreatedAt})`;
      } else if (
        this._currentBatchCreatedAt !== null &&
        incomingCreatedAt === this._currentBatchCreatedAt
      ) {
        // 情况3: 完全相同，跳过重复处理
        console.log(
          `⏭️ 跳过重复请求（batch_id: ${batchId}, createdAt: ${incomingCreatedAt}）`
        );
        return { success: false, results: new Map(), aborted: false };
      }
    }

    if (shouldAbort && this._currentAbortController) {
      console.log(`⚠️ 中断旧批次: ${abortReason}`);
      this._currentAbortController.abort();
    }

    // ✅ 设置新批次信息
    const abortController = new AbortController();
    this._currentBatchId = batchId;
    this._currentBatchCreatedAt = incomingCreatedAt;
    this._currentAbortController = abortController;
    const { signal } = abortController;

    try {
      options?.onProgress?.(0.05, '创建沙箱...');

      if (signal.aborted) {
        return { success: false, results: new Map(), aborted: true };
      }

      // ============================================
      // 步骤2: 创建沙箱并复制主文档状态
      // ============================================
      const sandboxDoc = new YDoc({ guid: `sandbox-${batchId}` });
      const mainDocUpdate = encodeStateAsUpdate(this.store.doc.spaceDoc);
      applyUpdate(sandboxDoc, mainDocUpdate);

      console.log(`✅ 沙箱创建完成: ${batchId}`);

      // ============================================
      // 步骤3: 异步准备所有 block 数据（可中断）
      // ============================================
      options?.onProgress?.(0.2, '准备 block 数据...');

      const preparedBlocksData = await this._prepareAllBlocksData(
        suggestions,
        signal
      );

      if (signal.aborted) {
        console.log(`❌ 批次 ${batchId} 在准备数据后被中断`);
        return { success: false, results: new Map(), aborted: true };
      }

      console.log(`✅ 数据准备完成: ${batchId}`);

      // ============================================
      // 步骤4: 在沙箱中应用所有更改（同步操作）
      // ============================================
      options?.onProgress?.(0.6, '在沙箱中应用更改...');

      const results = await this._applyChangesInSandbox(
        sandboxDoc,
        suggestions,
        preparedBlocksData,
        batchId,
        signal
      );

      if (signal.aborted) {
        console.log(`❌ 批次 ${batchId} 在应用更改时被中断`);
        return { success: false, results: new Map(), aborted: true };
      }

      // ============================================
      // 步骤5: 提交到主文档（原子操作）
      // ============================================
      if (signal.aborted) {
        console.log(`❌ 批次 ${batchId} 在提交前被中断`);
        return { success: false, results: new Map(), aborted: true };
      }

      options?.onProgress?.(0.9, '提交到主文档...');

      await this._commitSandboxToMain(sandboxDoc, signal);

      // 清理批次状态
      if (this._currentBatchId === batchId) {
        this._currentBatchId = null;
        this._currentBatchCreatedAt = null;
        this._currentAbortController = null;
      }

      options?.onProgress?.(1.0, '完成');

      console.log(`✅ 批次 ${batchId} 成功提交`);

      return {
        success: true,
        results,
      };
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log(`⚠️ 批次 ${batchId} 被中断`);
        return { success: false, results: new Map(), aborted: true };
      }

      console.error(`❌ 批次 ${batchId} 失败:`, error);
      return { success: false, results: new Map() };
    }
  }

  /**
   * 🔧 准备所有 blocks 的数据（异步，可中断）
   */
  private async _prepareAllBlocksData(
    suggestions: Suggestion[],
    signal: AbortSignal
  ): Promise<
    Map<
      string,
      {
        blockDataArray: Array<{
          id: string;
          flavour: string;
          props: any;
          snapshot: any;
        }>;
        parentId: string;
        insertIndex: number;
      }
    >
  > {
    const preparedData = new Map();

    for (const suggestion of suggestions) {
      // 🔑 检查中断
      if (signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      try {
        if (suggestion.type === 'delete') {
          preparedData.set(suggestion.id, {
            blockDataArray: [],
            parentId: '',
            insertIndex: 0,
          });
          continue;
        }

        const markdown = suggestion.chatCommand?.new_str;
        if (!markdown) {
          console.warn(`Suggestion ${suggestion.id} 缺少 new_str`);
          continue;
        }

        // 1. 计算插入位置
        const insertPosition = this._calculateInsertPosition(
          suggestion.targetBlockIds,
          suggestion.chatCommand
        );

        if (!insertPosition) {
          console.warn(`Suggestion ${suggestion.id} 无法确定插入位置`);
          continue;
        }

        // 2. 解析 markdown 到 block snapshots（异步）
        const { snapshot } = await this._markdownToSnapshot(markdown);

        // 🔑 解析后再次检查中断
        if (signal.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        if (!snapshot) {
          console.warn(`Suggestion ${suggestion.id} markdown 解析失败`);
          continue;
        }

        // 3. ✅ 提取 block snapshots（使用与旧 suggestion-applicator 相同的逻辑）
        const blockSnapshots = this._extractBlockSnapshots(snapshot);
        const blockDataArray: Array<{
          id: string;
          flavour: string;
          props: any;
          snapshot: any;
        }> = [];

        for (const blockSnapshot of blockSnapshots) {
          // 为每个 block 生成 ID
          const blockId = blockSnapshot.id || nanoid();

          blockDataArray.push({
            id: blockId,
            flavour: blockSnapshot.flavour,
            props: blockSnapshot.props || {},
            snapshot: blockSnapshot, // ✅ 保留完整 snapshot 用于创建 Y.Text
          });
        }

        preparedData.set(suggestion.id, {
          blockDataArray,
          parentId: insertPosition.parentId,
          insertIndex: insertPosition.index,
        });

        console.log(
          `✅ 准备完成 suggestion ${suggestion.id}: ${blockDataArray.length} blocks`
        );
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          throw error;
        }
        console.error(`准备 ${suggestion.id} 失败:`, error);
      }
    }

    return preparedData;
  }

  /**
   * 🔧 在沙箱中应用所有更改（手动操作 YDoc，正确处理 Y.Text）
   */
  private async _applyChangesInSandbox(
    sandboxDoc: YDoc,
    suggestions: Suggestion[],
    preparedBlocksData: Map<string, any>,
    _batchId: string,
    signal: AbortSignal
  ): Promise<Map<string, any>> {
    const sandboxBlocks = sandboxDoc.getMap('blocks');
    const sandboxSuggestionInfo = sandboxDoc.getMap('ySuggestionInfo');

    // 1. 获取现有 suggestions
    const existingSuggestions = this._getSuggestionsFromYMap(
      sandboxSuggestionInfo
    );

    // ✅ 不判断 batch_id，清理所有 pending suggestions
    const allOldPendingSuggestions = existingSuggestions.filter(
      (s: Suggestion) => s.status === 'pending'
    );

    if (allOldPendingSuggestions.length > 0) {
      console.log(
        `🧹 清理所有 ${allOldPendingSuggestions.length} 个旧的 pending suggestions`
      );

      allOldPendingSuggestions.forEach((s: Suggestion) => {
        // 删除 newBlocks
        s.newBlockIds?.forEach(blockId => {
          this._deleteBlockInSandbox(sandboxBlocks, blockId);
        });

        // 清理 targetBlocks 的 suggestionState
        s.targetBlockIds.forEach(blockId => {
          const block = sandboxBlocks.get(blockId);
          if (block) {
            (block as any).delete('suggestionState');
          }
        });
      });
    }

    // ✅ 新增：追踪每个 targetBlock 后面已插入的 blocks 数量
    // key: targetBlockIds 的组合键，value: 已插入的 blocks 数量
    const insertOffsetMap = new Map<string, number>();
    const positionCacheByLine = new Map<
      number,
      { parentId: string; index: number }
    >();

    // 3. 创建新 blocks 并记录结果
    const results = new Map<string, any>();

    for (const suggestion of suggestions) {
      // 🔑 每个 suggestion 处理前检查中断
      if (signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      const preparedData = preparedBlocksData.get(suggestion.id);

      if (!preparedData) {
        results.set(suggestion.id, {
          suggestionId: suggestion.id,
          targetBlockIds: suggestion.targetBlockIds,
          newBlockIds: [],
          batch_id: suggestion.batch_id,
          doc_id: this.store.id,
          status: 'error',
        });
        continue;
      }

      const newBlockIds: string[] = [];

      // ✅ 在沙箱中动态计算插入位置（因为前面的 suggestions 可能已经改变了文档结构）
      let insertPosition = {
        parentId: preparedData.parentId,
        insertIndex: preparedData.insertIndex,
      };

      if (
        suggestion.type !== 'delete' &&
        suggestion.targetBlockIds.length > 0
      ) {
        const dynamicPosition = this._calculateInsertPositionInSandbox(
          sandboxBlocks,
          suggestion.targetBlockIds,
          suggestion.chatCommand
        );

        const startLine = Number(suggestion.chatCommand?.start_line_number);

        if (dynamicPosition) {
          const targetKey = suggestion.targetBlockIds.join(',');
          let currentOffset = insertOffsetMap.get(targetKey) || 0;
          let basePosition = dynamicPosition;

          if (!Number.isNaN(startLine)) {
            const cached = positionCacheByLine.get(startLine);
            if (cached) {
              basePosition = cached;
            } else {
              positionCacheByLine.set(startLine, basePosition);
            }
          }

          insertPosition = {
            parentId: basePosition.parentId,
            insertIndex: basePosition.index + currentOffset,
          };
          console.log(
            `🔧 动态计算插入位置 suggestion ${suggestion.id}: ` +
              `parent=${basePosition.parentId}, ` +
              `baseIndex=${basePosition.index}, ` +
              `offset=${currentOffset}, ` +
              `finalIndex=${insertPosition.insertIndex}`
          );
        }
      }

      // ✅ 手动创建 blocks（正确处理 Y.Text）
      if (suggestion.type !== 'delete' && preparedData.blockDataArray) {
        for (
          let index = 0;
          index < preparedData.blockDataArray.length;
          index++
        ) {
          const blockData = preparedData.blockDataArray[index];
          const blockId = await this._createBlockInSandbox(
            sandboxBlocks,
            blockData,
            insertPosition.parentId,
            insertPosition.insertIndex + index
          );

          if (blockId) {
            // 设置 suggestionState
            this._setSuggestionStateInSandbox(
              sandboxBlocks,
              blockId,
              suggestion.id,
              'new'
            );
            newBlockIds.push(blockId);
          }
        }

        // ✅ 新增：更新偏移量（累加本次插入的 blocks 数量）
        if (newBlockIds.length > 0 && suggestion.targetBlockIds.length > 0) {
          const targetKey = suggestion.targetBlockIds.join(',');
          const currentOffset = insertOffsetMap.get(targetKey) || 0;
          insertOffsetMap.set(targetKey, currentOffset + newBlockIds.length);
          console.log(
            `📊 更新偏移量 targetKey=${targetKey}: ${currentOffset} → ${currentOffset + newBlockIds.length}`
          );
        }
      }

      // 标记 target blocks
      suggestion.targetBlockIds.forEach(blockId => {
        this._setSuggestionStateInSandbox(
          sandboxBlocks,
          blockId,
          suggestion.id,
          'old'
        );
      });

      results.set(suggestion.id, {
        suggestionId: suggestion.id,
        targetBlockIds: suggestion.targetBlockIds,
        newBlockIds,
        batch_id: suggestion.batch_id,
        doc_id: this.store.id,
        status: 'pending',
      });
    }

    // 4. 更新 ySuggestionInfo
    const updatedSuggestions = suggestions.map(s => {
      const result = results.get(s.id);
      return {
        ...s,
        status: 'pending' as const,
        newBlockIds: result?.newBlockIds || [],
      };
    });

    // ✅ 直接覆盖，因为新批次会取消旧批次
    // UIManager 会自动清理不在列表中的 suggestions
    sandboxSuggestionInfo.set('list', { list: updatedSuggestions });

    console.log(`✅ 沙箱操作完成: ${results.size} suggestions`);

    return results;
  }

  /**
   * 🔧 提交沙箱到主文档（原子操作）
   */
  private async _commitSandboxToMain(
    sandboxDoc: YDoc,
    signal: AbortSignal
  ): Promise<void> {
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    // 计算差异
    const mainStateVector = encodeStateVector(this.store.doc.spaceDoc);
    const sandboxDiff = encodeStateAsUpdate(sandboxDoc, mainStateVector);

    console.log(`📊 差异大小: ${sandboxDiff.length} bytes`);

    // ✅ 在单个 transaction 中应用（原子操作）
    this.store.transact(() => {
      applyUpdate(this.store.doc.spaceDoc, sandboxDiff);
    });

    this.store.captureSync();

    console.log(`✅ 差异已提交到主文档`);
  }

  /**
   * 🔧 计算插入位置（基于主文档）
   */
  private _calculateInsertPosition(
    targetBlockIds: string[],
    chatCommand: any
  ): { parentId: string; index: number } | null {
    if (!targetBlockIds.length) return null;

    const lastTargetBlockId = targetBlockIds[targetBlockIds.length - 1];
    const block = this.store.getBlock(lastTargetBlockId);
    if (!block) return null;

    const parent = this.store.getParent(lastTargetBlockId);
    if (!parent) return null;

    const childIndex = parent.children.indexOf(block.model);
    const position =
      Number(chatCommand?.start_line_number) <= 0 ? 'before' : 'after';
    const insertIndex = Math.max(
      position === 'before' ? childIndex : childIndex + 1,
      0
    );

    return {
      parentId: parent.id,
      index: insertIndex,
    };
  }

  /**
   * ✅ 在沙箱中动态计算插入位置
   * 用于处理多个 suggestions 依次应用时，文档结构已被前面的 suggestions 改变的情况
   */
  private _calculateInsertPositionInSandbox(
    sandboxBlocks: YMap<any>,
    targetBlockIds: string[],
    chatCommand: any
  ): { parentId: string; index: number } | null {
    if (!targetBlockIds.length) return null;

    const lastTargetBlockId = targetBlockIds[targetBlockIds.length - 1];
    const targetBlock = sandboxBlocks.get(lastTargetBlockId);

    if (!targetBlock) {
      console.warn(`🔍 沙箱中未找到 target block ${lastTargetBlockId}`);
      return null;
    }

    // 查找父 block
    let parentBlock: any = null;
    let parentId: string = '';

    sandboxBlocks.forEach((block: any, blockId: string) => {
      const children = block.get('sys:children');
      if (children instanceof YArray) {
        const childArray = children.toArray();
        if (childArray.includes(lastTargetBlockId)) {
          parentBlock = block;
          parentId = blockId;
        }
      }
    });

    if (!parentBlock || !parentId) {
      console.warn(`🔍 沙箱中未找到 parent block for ${lastTargetBlockId}`);
      return null;
    }

    // 查找 target block 在 parent children 中的位置
    const children = parentBlock.get('sys:children');
    if (!(children instanceof YArray)) {
      return null;
    }

    const childArray = children.toArray();
    const childIndex = childArray.indexOf(lastTargetBlockId);

    if (childIndex === -1) {
      return null;
    }

    const position =
      Number(chatCommand?.start_line_number) <= 0 ? 'before' : 'after';
    const insertIndex = Math.max(
      position === 'before' ? childIndex : childIndex + 1,
      0
    );

    return {
      parentId,
      index: insertIndex,
    };
  }

  /**
   * 🔧 解析 markdown 到 snapshot
   */
  private async _markdownToSnapshot(
    markdown: string
  ): Promise<{ snapshot: any; transformer: any }> {
    const transformer = this.store.getTransformer([
      titleMiddleware(this.store.workspace.meta.docMetas),
      embedSyncedDocMiddleware('content'),
    ]);

    // ✅ 关键步骤：手动建立 pathBlobIdMap
    const assetsManager = transformer.assetsManager;
    const pathBlobIdMap = assetsManager.getPathBlobIdMap();

    // 从 markdown 中提取所有 assets/ 路径的图片
    const assetPathRegex = /!\[.*?\]\((assets\/([^)]+?))\)/g;
    let match;

    while ((match = assetPathRegex.exec(markdown)) !== null) {
      const fullPath = match[1]; // "assets/JTH...=.bin"
      const filename = match[2]; // "JTH...=.bin"

      // 提取 blob ID（去掉 .bin 等扩展名）
      const blobId = filename.replace(/\.[^.]+$/, ''); // "JTH...="

      // 设置映射：让 processImageNodeToBlock 能找到
      pathBlobIdMap.set(fullPath, blobId);
      pathBlobIdMap.set(filename, blobId);

      console.log(`🔧 设置路径映射: ${fullPath} -> ${blobId}`);
    }

    const adapter = new MixTextAdapter(transformer, this.store.provider);
    const snapshot = await adapter.toBlockSnapshotInSliceStruct({
      file: markdown,
      assets: assetsManager,
      workspaceId: this.store.workspace.id,
      pageId: this.store.id,
    });

    return { snapshot, transformer };
  }

  /**
   * ✅ 提取 block snapshots（与旧 suggestion-applicator 逻辑一致）
   * 这个方法会将多行 markdown 正确解析为多个独立的 block
   */
  private _extractBlockSnapshots(snapshot: any): any[] {
    return snapshot?.content.flatMap((x: any) => x.children) ?? [];
  }

  /**
   * 🔧 在沙箱中创建 block（直接使用 Transformer）
   */
  private async _createBlockInSandbox(
    sandboxBlocks: YMap<any>,
    blockData: { id: string; flavour: string; props: any; snapshot: any },
    parentId: string,
    index: number
  ): Promise<string | null> {
    try {
      const { id, flavour, props: snapshotProps, snapshot } = blockData;

      // 🎯 获取 schema
      const schema = this.store.schema.get(flavour);
      if (!schema) {
        console.warn(`Schema not found for flavour: ${flavour}`);
        return this._createBlockInSandboxFallback(
          sandboxBlocks,
          blockData,
          parentId,
          index
        );
      }

      // 🎯 创建 YBlock
      const yBlock = new YMap();
      yBlock.set('sys:id', id);
      yBlock.set('sys:flavour', flavour);
      yBlock.set('sys:version', schema.version);
      yBlock.set('sys:children', new YArray());

      // 🎯 使用 Transformer 处理 props
      const transformer =
        schema.transformer?.(new Map()) ?? new BaseBlockTransformer(new Map());

      try {
        const { props: processedProps } = await transformer.fromSnapshot({
          json: {
            id,
            flavour,
            props: snapshotProps,
          },
          assets: this.store.getTransformer().assetsManager,
          children: snapshot?.children ?? [],
        });

        // 获取默认 props 并合并
        const defaultProps = schema.model.props?.(internalPrimitives) ?? {};
        const finalProps = {
          ...defaultProps,
          ...processedProps,
        };

        // 删除系统属性
        delete finalProps.id;
        delete finalProps.flavour;
        delete finalProps.children;

        // 🎯 使用与官方 addBlock 相同的逻辑处理 props
        const isFlatData = schema.model.isFlatData;
        if (isFlatData) {
          const run = (obj: unknown, basePath: string) => {
            if (isPureObject(obj)) {
              Object.entries(obj).forEach(([key, value]) => {
                const fullPath = basePath ? `${basePath}.${key}` : key;
                run(value, fullPath);
              });
            } else {
              yBlock.set(`prop:${basePath}`, native2Y(obj));
            }
          };
          run(finalProps, '');
        } else {
          Object.entries(finalProps).forEach(([key, value]) => {
            if (value === undefined) return;
            yBlock.set(`prop:${key}`, native2Y(value));
          });
        }

        console.log(`🎯 成功使用 Transformer 处理 block ${id} (${flavour})`);
      } catch (error) {
        console.warn(`Transformer 处理失败，使用简单方式:`, error);

        // 降级：简单处理 props
        const defaultProps = schema.model.props?.(internalPrimitives) ?? {};
        const props = {
          ...defaultProps,
          ...snapshotProps,
        };

        delete props.id;
        delete props.flavour;
        delete props.children;

        Object.entries(props).forEach(([key, value]) => {
          if (value === undefined) return;
          yBlock.set(`prop:${key}`, native2Y(value));
        });
      }

      // 🎯 添加到沙箱
      sandboxBlocks.set(id, yBlock);

      // 🔧 更新父 block 的 children
      if (parentId) {
        const parentBlock = sandboxBlocks.get(parentId);
        if (parentBlock) {
          const children = parentBlock.get('sys:children');
          if (children instanceof YArray) {
            const safeIndex = Math.min(index, children.length);
            children.insert(safeIndex, [id]);
          }
        }
      }

      // 🔧 递归创建子 blocks
      if (snapshot?.children && Array.isArray(snapshot.children)) {
        for (let i = 0; i < snapshot.children.length; i++) {
          const childSnapshot = snapshot.children[i];
          if (childSnapshot && childSnapshot.id && childSnapshot.flavour) {
            await this._createBlockInSandbox(
              sandboxBlocks,
              {
                id: childSnapshot.id,
                flavour: childSnapshot.flavour,
                props: childSnapshot.props || {},
                snapshot: childSnapshot,
              },
              id, // 使用当前 block 作为父级
              i
            );
          }
        }
      }

      return id;
    } catch (error) {
      console.error(`创建沙箱 block 失败:`, error);
      return this._createBlockInSandboxFallback(
        sandboxBlocks,
        blockData,
        parentId,
        index
      );
    }
  }

  /**
   * 🔧 降级方法：当官方方法失败时使用
   */
  private _createBlockInSandboxFallback(
    sandboxBlocks: YMap<any>,
    blockData: { id: string; flavour: string; props: any; snapshot: any },
    parentId: string,
    index: number
  ): string | null {
    try {
      console.warn(`使用降级方法创建 block ${blockData.id}`);

      const blockYMap = new YMap();
      blockYMap.set('sys:id', blockData.id);
      blockYMap.set('sys:flavour', blockData.flavour);
      blockYMap.set('sys:children', new YArray());

      // 简单的 props 处理
      Object.entries(blockData.props || {}).forEach(([key, value]) => {
        if (key !== 'children' && key !== 'id' && key !== 'flavour') {
          try {
            blockYMap.set(`prop:${key}`, native2Y(value));
          } catch (error) {
            console.warn(`Failed to convert prop ${key}:`, error);
            blockYMap.set(`prop:${key}`, value);
          }
        }
      });

      sandboxBlocks.set(blockData.id, blockYMap);

      // 更新父 block 的 children
      if (parentId) {
        const parentBlock = sandboxBlocks.get(parentId);
        if (parentBlock) {
          const children = parentBlock.get('sys:children');
          if (children instanceof YArray) {
            const safeIndex = Math.min(index, children.length);
            children.insert(safeIndex, [blockData.id]);
          }
        }
      }

      return blockData.id;
    } catch (error) {
      console.error(`降级方法也失败了:`, error);
      return null;
    }
  }

  /**
   * 🔧 在沙箱中删除 block
   */
  private _deleteBlockInSandbox(
    sandboxBlocks: YMap<any>,
    blockId: string
  ): void {
    try {
      const block = sandboxBlocks.get(blockId);
      if (!block) return;

      // 从父 block 的 children 中移除
      sandboxBlocks.forEach((parentBlock: any) => {
        const children = parentBlock.get('sys:children');

        if (children) {
          // ✅ 处理 YArray 或普通数组
          if (children instanceof YArray) {
            // YArray: 找到索引并删除
            const childrenArray = children.toArray();
            const indexToDelete = childrenArray.indexOf(blockId);
            if (indexToDelete !== -1) {
              children.delete(indexToDelete, 1); // 从 index 删除 1 个元素
            }
          } else if (Array.isArray(children) && children.includes(blockId)) {
            // 普通数组：过滤后重新设置
            const newChildren = children.filter((id: string) => id !== blockId);
            parentBlock.set('sys:children', newChildren);
          }
        }
      });

      // 递归删除子 blocks
      const children = block.get('sys:children');
      if (children) {
        // ✅ 处理 YArray 或普通数组
        const childrenArray =
          children instanceof YArray
            ? children.toArray()
            : Array.isArray(children)
              ? children
              : [];

        childrenArray.forEach((childId: string) => {
          this._deleteBlockInSandbox(sandboxBlocks, childId);
        });
      }

      // 删除 block 本身
      sandboxBlocks.delete(blockId);
    } catch (error) {
      console.error(`删除沙箱 block 失败:`, error);
    }
  }

  /**
   * 🔧 在沙箱中设置 suggestionState
   */
  private _setSuggestionStateInSandbox(
    sandboxBlocks: YMap<any>,
    blockId: string,
    suggestionId: string,
    type: 'old' | 'new'
  ): void {
    try {
      const block = sandboxBlocks.get(blockId);
      if (block) {
        block.set('suggestionState', {
          id: suggestionId,
          type,
          createdAt: Date.now(),
          status: 'pending',
        });
      }
    } catch (error) {
      console.error(`设置沙箱 suggestionState 失败:`, error);
    }
  }

  /**
   * 🔧 从 YMap 中读取 suggestions
   */
  private _getSuggestionsFromYMap(ySuggestionInfo: YMap<any>): Suggestion[] {
    try {
      const data = ySuggestionInfo.get('list');
      return data?.list || [];
    } catch {
      return [];
    }
  }

  /**
   * 🔧 手动取消当前批次
   */
  cancelCurrentBatch(): void {
    if (this._currentBatchId && this._currentAbortController) {
      console.log(
        `⚠️ 手动取消批次: ${this._currentBatchId} (createdAt: ${this._currentBatchCreatedAt})`
      );
      this._currentAbortController.abort();
      this._currentBatchId = null;
      this._currentBatchCreatedAt = null;
      this._currentAbortController = null;
    }
  }

  /**
   * 🔍 获取当前批次状态
   */
  getCurrentBatchStatus(): {
    batchId: string | null;
    createdAt: number | null;
    isProcessing: boolean;
  } {
    return {
      batchId: this._currentBatchId,
      createdAt: this._currentBatchCreatedAt,
      isProcessing: this._currentBatchId !== null,
    };
  }
}
